import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import type { RegistryAllocation } from '../../api/registryEntityTypes';
import type { Offer } from '../../data/types';
import { formatAmount } from '../../entities/entityFormat';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';

/** The sample offer whose fund matches the allocation's (mock) fund opportunity, else the first. */
export function defaultOfferId(offers: Offer[], allocation: RegistryAllocation): string {
  const match = offers.find(
    (offer) => `fo-${offer.fundId}` === allocation.fundOpportunity.externalId,
  );
  return (match ?? offers[0])?.id ?? '';
}

/**
 * Starts a (sample) application from an allocation. Offers are not built yet (milestone 4), so the
 * application is filed under one of the prototype's sample offers.
 */
export default function StartApplicationDialog({
  allocation,
  email,
  onClose,
}: {
  allocation: RegistryAllocation | null;
  email?: string;
  onClose: () => void;
}) {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const offers = scope.offers.filter((offer) => offer.status === 'PUBLISHED');
  const [offerId, setOfferId] = useState('');

  useEffect(() => {
    if (allocation) setOfferId(defaultOfferId(offers, allocation));
    // Only when a different allocation is opened; offers are stable while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocation]);

  const start = () => {
    if (!allocation || !offerId) return;
    dispatch({ type: 'APPLICATION_START_FROM_ALLOCATION', allocation, offerId, email });
    toast('Sample application started from the allocation', 'success');
    onClose();
  };

  return (
    <Dialog open={Boolean(allocation)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Start an application</DialogTitle>
      {allocation && (
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {allocation.investmentEntityName} is allocated{' '}
            <strong>{formatAmount(allocation.allocatedAmount)}</strong> in{' '}
            {allocation.fundOpportunity.name} (investor {allocation.contact.name}). The application
            is sample data until applications are built; the allocation is not changed.
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Sample offer"
            value={offerId}
            onChange={(event) => setOfferId(event.target.value)}
            helperText={offers.length ? undefined : 'No published sample offer to apply into'}
          >
            {offers.map((offer) => (
              <MenuItem key={offer.id} value={offer.id}>
                {offer.title}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={start} disabled={!offerId}>
          Start application
        </Button>
      </DialogActions>
    </Dialog>
  );
}
